import { AutocompleteItem } from '@delmonicos/shared-design-system-react/src/components/autocomplete-input/autocomplete-input.component';
import { GetTransactionsStatisticsModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/statistics/models/input/get-transactions-statistics.model';
import { DateArg, formatISO } from 'date-fns';
import { createContext, useContext, useMemo, useState } from 'react';

import {
  StatisticsAreaGranularityEnum,
  StatisticsTimeGranularityEnum,
} from '@model/enum/statistics-granularity.enum';

export type StatisticsFilters = {
  areaGranularity?: StatisticsAreaGranularityEnum;
  selectedAreaGranularity: AutocompleteItem | null;
  timeGranularity?: StatisticsTimeGranularityEnum;
  selectedTimeGranularity?: {
    start: DateArg<Date> | undefined;
    end: DateArg<Date> | undefined;
  };
};

type FiltersContextType = {
  filters: StatisticsFilters;
  transformedFilters?: GetTransactionsStatisticsModel;
  setFilters: (filters: StatisticsFilters) => void;
};

const defaultFilters: StatisticsFilters = {
  areaGranularity: undefined,
  selectedAreaGranularity: null,
  selectedTimeGranularity: undefined,
  timeGranularity: undefined,
};

const FiltersContext = createContext<FiltersContextType | undefined>(undefined);

export const FiltersProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [filters, setFilters] = useState<StatisticsFilters>(defaultFilters);

  const transformedFilters: GetTransactionsStatisticsModel = useMemo(() => {
    return {
      networkId:
        filters.areaGranularity === StatisticsAreaGranularityEnum.Network &&
        filters.selectedAreaGranularity
          ? filters.selectedAreaGranularity.key
          : undefined,
      locationId:
        filters.areaGranularity === StatisticsAreaGranularityEnum.Location &&
        filters.selectedAreaGranularity
          ? filters.selectedAreaGranularity.key
          : undefined,
      chargePointId:
        filters.areaGranularity === StatisticsAreaGranularityEnum.ChargePoint &&
        filters.selectedAreaGranularity
          ? filters.selectedAreaGranularity.key
          : undefined,
      transactionEndedAtAfter: filters.selectedTimeGranularity?.start
        ? formatISO(filters.selectedTimeGranularity.start)
        : undefined,
      transactionEndedAtBefore: filters.selectedTimeGranularity?.end
        ? formatISO(filters.selectedTimeGranularity.end)
        : undefined,
    };
  }, [filters]);

  const contextValue = useMemo(
    () => ({ filters, setFilters, transformedFilters }),
    [filters, transformedFilters],
  );

  return (
    <FiltersContext.Provider value={contextValue}>
      {children}
    </FiltersContext.Provider>
  );
};

export const useFilters = (): FiltersContextType => {
  const context = useContext(FiltersContext);
  if (!context) {
    throw new Error('useFilters must be used within a FiltersProvider');
  }
  return context;
};
